import React, { useEffect, useState } from "react";
import Card from "../components/Card.js";
import {
  getShoplists,
  createShoplist,
  updateShoplist,
  deleteShoplist,
  toggleProductInShoplist,
  getShoplistProducts,
} from "../services/shoplist.services.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

const PER_PAGE = 20;

function Shoplist() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [shoplists, setShoplists] = useState([]);
  const [activeShoplistId, setActiveShoplistId] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingShoplists, setLoadingShoplists] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [page, setPage] = useState(1);

  const [newTitle, setNewTitle] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);

  const fetchShoplists = async () => {
    try {
      setLoadingShoplists(true);
      const res = await getShoplists();
      if (res?.success) {
        const list = res.data || [];
        setShoplists(list);
        if (list.length > 0) {
          setActiveShoplistId((current) => {
            const exists = list.some((item) => String(item._id) === String(current));
            if (exists && current) return current;
            const withProducts = list.find((item) => (item.totalProducts || 0) > 0);
            return withProducts ? String(withProducts._id) : String(list[0]._id);
          });
        } else {
          setActiveShoplistId(null);
          setProducts([]);
        }
      }
    } catch (err) {
      console.error("Error loading shoplists:", err);
    } finally {
      setLoadingShoplists(false);
    }
  };

  const fetchProducts = async (shoplistId) => {
    if (!shoplistId) {
      setProducts([]);
      return;
    }
    try {
      setLoadingProducts(true);
      const res = await getShoplistProducts(shoplistId);
      if (res?.success) {
        setProducts(res.data?.products || []);
      }
    } catch (err) {
      console.error("Error loading shoplist products:", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchShoplists();
  }, []);

  useEffect(() => {
    if (activeShoplistId) {
      setPage(1);
      fetchProducts(activeShoplistId);
    }
  }, [activeShoplistId]);

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) return;

    try {
      setIsCreating(true);
      const res = await createShoplist({ title: newTitle.trim() });
      if (res?.success) {
        setNewTitle("");
        setShowCreateInput(false);
        Swal.fire({
          icon: "success",
          text: t("shoplist.created", { defaultValue: "Shoplist created successfully!" }),
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });
        const createdId = res.data?._id;
        await fetchShoplists();
        if (createdId) {
          setActiveShoplistId(createdId);
        }
      }
    } catch (err) {
      console.error("Error creating shoplist:", err);
      Swal.fire({
        icon: "error",
        text: err?.response?.data?.message || "Failed to create shoplist",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleRename = async (list) => {
    const { value: title } = await Swal.fire({
      title: t("shoplist.rename", { defaultValue: "Rename Shoplist" }),
      input: "text",
      inputValue: list.title,
      showCancelButton: true,
      confirmButtonText: t("common.save", { defaultValue: "Save" }),
      cancelButtonText: t("common.cancel", { defaultValue: "Cancel" }),
      confirmButtonColor: "var(--secondary-500, #FF63B5)",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return t("shoplist.title_required", { defaultValue: "Shoplist title cannot be empty!" });
        }
      },
    });

    if (title && title.trim() !== list.title) {
      try {
        const res = await updateShoplist(list._id, { title: title.trim() });
        if (res?.success) {
          Swal.fire({
            icon: "success",
            text: t("shoplist.renamed", { defaultValue: "Shoplist renamed!" }),
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });
          fetchShoplists();
        }
      } catch (err) {
        console.error("Error updating shoplist:", err);
      }
    }
  };

  const handleDelete = async (list) => {
    const result = await Swal.fire({
      title: t("shoplist.delete_confirm_title", { defaultValue: "Delete Shoplist?" }),
      text: t("shoplist.delete_confirm_desc", {
        defaultValue: `Are you sure you want to delete "${list.title}"?`,
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("common.delete", { defaultValue: "Delete" }),
      cancelButtonText: t("common.cancel", { defaultValue: "Cancel" }),
    });

    if (result.isConfirmed) {
      try {
        const res = await deleteShoplist(list._id);
        if (res?.success) {
          Swal.fire({
            icon: "success",
            text: t("shoplist.deleted", { defaultValue: "Shoplist deleted" }),
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });
          await fetchShoplists();
        }
      } catch (err) {
        console.error("Error deleting shoplist:", err);
      }
    }
  };

  const handleRemoveProduct = async (product) => {
    if (!activeShoplistId) return;
    try {
      const res = await toggleProductInShoplist({
        productId: product._id,
        shoplistId: activeShoplistId,
      });

      if (res?.success) {
        Swal.fire({
          icon: "success",
          text: res.message || "Product removed from shoplist",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
        setProducts((prev) => prev.filter((p) => p._id !== product._id));
        setShoplists((prev) =>
          prev.map((item) =>
            item._id === activeShoplistId
              ? { ...item, totalProducts: Math.max(0, (item.totalProducts || 1) - 1) }
              : item
          )
        );
      }
    } catch (err) {
      console.error("Error removing product from shoplist:", err);
    }
  };

  const [mobileView, setMobileView] = useState("products"); // "list" | "products"

  const activeShoplistObj = shoplists.find((s) => String(s._id) === String(activeShoplistId));
  const totalPages = Math.ceil(products.length / PER_PAGE);
  const currentItems = products.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const getPaginationPages = (currentPage, totalPages) => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 2) {
      return [1, 2, "...", totalPages];
    }
    if (currentPage >= totalPages - 1) {
      return [1, "...", totalPages - 1, totalPages];
    }
    return [1, "...", currentPage, "...", totalPages];
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-1.5 sm:gap-2 mt-10 pb-8 flex-wrap">
        <button
          disabled={page <= 1}
          onClick={() => {
            setPage((p) => p - 1);
            window.scrollTo({ top: 300, behavior: "smooth" });
          }}
          className="px-3.5 py-1.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs"
        >
          {t("common.prev")}
        </button>
        {getPaginationPages(page, totalPages).map((item, idx) =>
          item === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-sm font-bold text-gray-400 select-none">
              …
            </span>
          ) : (
            <button
              key={`page-${item}`}
              onClick={() => {
                setPage(item);
                window.scrollTo({ top: 300, behavior: "smooth" });
              }}
              className={`min-w-[36px] h-9 px-3 py-1 text-sm font-semibold rounded-xl border transition shadow-2xs ${
                item === page
                  ? "bg-[var(--secondary-500,#FF63B5)] text-white border-[var(--secondary-500,#FF63B5)] shadow-xs"
                  : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
              }`}
            >
              {item}
            </button>
          )
        )}
        <button
          disabled={page >= totalPages}
          onClick={() => {
            setPage((p) => p + 1);
            window.scrollTo({ top: 300, behavior: "smooth" });
          }}
          className="px-3.5 py-1.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs"
        >
          {t("common.next")}
        </button>
      </div>
    );
  };

  if (loadingShoplists) {
    return (
      <div className="flex items-center justify-center py-24 min-h-[60vh]">
        <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="shoplist-page py-8 min-h-screen bg-gray-50/40">
      <div className="container mx-auto px-4">
        {/* Header Bar */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {t("shoplist.page_title", { defaultValue: "My Shoplist" })}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t("shoplist.page_subtitle", {
                defaultValue: "Organize your favourite products by custom shoplists.",
              })}
            </p>
          </div>

          {!showCreateInput && (
            <button
              type="button"
              onClick={() => setShowCreateInput(true)}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-[var(--secondary-500,#FF63B5)] text-white hover:opacity-95 shadow-md shadow-pink-200 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              {t("shoplist.create_new_tab", { defaultValue: "Create Shoplist" })}
            </button>
          )}
        </div>

        {/* Inline Shoplist Creation Input */}
        {showCreateInput && (
          <form onSubmit={handleCreate} className="mb-6 p-4 bg-pink-50/70 rounded-2xl border border-pink-100 flex items-center gap-3">
            <input
              type="text"
              autoFocus
              placeholder={t("shoplist.enter_title_placeholder", {
                defaultValue: "Enter shoplist title (e.g. Summer Wear, Gadgets, Wishlist)...",
              })}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              maxLength={40}
              className="flex-1 text-sm px-4 py-2.5 rounded-xl border border-gray-300 focus:outline-none focus:border-pink-500 bg-white shadow-sm"
            />
            <button
              type="submit"
              disabled={isCreating || !newTitle.trim()}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-[var(--secondary-500,#FF63B5)] hover:opacity-95 rounded-xl disabled:opacity-50 transition shadow-sm shrink-0"
            >
              {isCreating ? t("common.creating", "Creating...") : t("common.create", "Create")}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreateInput(false);
                setNewTitle("");
              }}
              className="px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 rounded-xl transition shrink-0"
            >
              {t("common.cancel", "Cancel")}
            </button>
          </form>
        )}

        {shoplists.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200 shadow-sm mx-auto max-w-xl">
            <div className="w-16 h-16 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-4 text-pink-500">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-1">
              {t("shoplist.no_shoplists_yet", { defaultValue: "You don't have any shoplists yet!" })}
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6 px-4">
              {t("shoplist.create_first_instruction", {
                defaultValue: "Create your first shoplist to organize and collect your favourite products.",
              })}
            </p>
            <button
              type="button"
              onClick={() => setShowCreateInput(true)}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-[var(--secondary-500,#FF63B5)] hover:opacity-95 rounded-xl shadow-md shadow-pink-200 transition"
            >
              {t("shoplist.create_new_tab", { defaultValue: "Create Shoplist" })}
            </button>
          </div>
        ) : (
          /* Spotify-Style Split Layout: Shoplist Series on Left, Products on Right */
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Custom Scrollbar Styles */}
            <style>{`
              .shoplist-scrollbar::-webkit-scrollbar {
                width: 6px;
              }
              .shoplist-scrollbar::-webkit-scrollbar-track {
                background: #f3f4f6;
                border-radius: 8px;
              }
              .shoplist-scrollbar::-webkit-scrollbar-thumb {
                background: #f472b6;
                border-radius: 8px;
              }
              .shoplist-scrollbar::-webkit-scrollbar-thumb:hover {
                background: #ec4899;
              }
            `}</style>

            {/* Left Column: Playlist Series (Numbered 1, 2, 3...) */}
            <div
              className={`w-full lg:w-80 xl:w-96 shrink-0 bg-white rounded-2xl p-4 border border-gray-200/80 shadow-xs lg:sticky lg:top-24 ${
                mobileView === "products" ? "hidden lg:block" : "block"
              }`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-gray-900">
                    {t("shoplist.playlists_title", { defaultValue: "Shoplists" })}
                  </h3>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-pink-100 text-pink-600">
                    {shoplists.length}
                  </span>
                </div>
              </div>

              {/* Playlist Series List */}
              <div className="flex flex-col gap-2 max-h-[calc(100vh-230px)] min-h-[250px] overflow-y-auto pr-1.5 shoplist-scrollbar">
                {shoplists.map((list, index) => {
                  const isActive = String(list._id) === String(activeShoplistId);
                  return (
                    <div
                      key={list._id}
                      onClick={() => {
                        setActiveShoplistId(String(list._id));
                        setMobileView("products");
                      }}
                      className={`group flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200 border select-none ${
                        isActive
                          ? "bg-pink-50/90 border-pink-300 shadow-sm"
                          : "bg-white border-transparent hover:bg-gray-50 hover:border-gray-200"
                      }`}
                    >
                      {/* Number circle: (1), (2), (3)... matching Spotify tracklist style */}
                      <span
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition ${
                          isActive
                            ? "bg-[var(--secondary-500,#FF63B5)] text-white shadow-xs"
                            : "bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-800"
                        }`}
                      >
                        {index + 1}
                      </span>

                      {/* Cover image or gradient placeholder */}
                      <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-100 border border-gray-200/80 relative shadow-xs">
                        {list.coverImage ? (
                          <img
                            src={list.coverImage}
                            alt={list.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-pink-400 via-rose-400 to-purple-500 flex items-center justify-center text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 opacity-90">
                              <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3.5 7.688 3.5c1.607 0 3.125.688 4.312 1.83 1.187-1.142 2.705-1.83 4.312-1.83 2.974 0 5.438 1.822 5.438 4.75 0 3.924-2.438 7.11-4.739 9.267a25.18 25.18 0 01-4.244 3.17 15.255 15.255 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Playlist info */}
                      <div className="flex-1 min-w-0">
                        <h4
                          className={`text-sm font-bold truncate leading-tight ${
                            isActive ? "text-pink-600" : "text-gray-900"
                          }`}
                        >
                          {list.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {list.totalProducts || 0}{" "}
                          {list.totalProducts === 1
                            ? t("shoplist.product", "product")
                            : t("shoplist.products", "products")}
                        </p>
                      </div>

                      {/* Action buttons: Rename & Delete */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          title={t("common.rename", { defaultValue: "Rename" })}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRename(list);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                            <path d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z" />
                            <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          title={t("common.delete", { defaultValue: "Delete" })}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(list);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                            <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5a.75.75 0 0 1 .75-.75Zm3.84.75a.75.75 0 0 0-1.5 0v6.5a.75.75 0 0 0 1.5 0v-6.5Z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Products of Selected Playlist */}
            <div
              className={`flex-1 min-w-0 w-full ${
                mobileView === "list" ? "hidden lg:block" : "block"
              }`}
            >
              {/* Mobile Back Button */}
              <div className="lg:hidden mb-4">
                <button
                  type="button"
                  onClick={() => setMobileView("list")}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900 px-3.5 py-2 rounded-xl bg-white border border-gray-200 shadow-xs transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                  </svg>
                  {t("shoplist.back_to_playlists", { defaultValue: "Back to Shoplists" })}
                </button>
              </div>

              {/* Selected Playlist Hero Card */}
              {activeShoplistObj && (
                <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-200/80 shadow-xs mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden shrink-0 shadow-sm bg-gray-100 border border-gray-200/80">
                      {activeShoplistObj.coverImage ? (
                        <img
                          src={activeShoplistObj.coverImage}
                          alt={activeShoplistObj.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-pink-400 via-rose-400 to-purple-500 flex items-center justify-center text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 opacity-90">
                            <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3.5 7.688 3.5c1.607 0 3.125.688 4.312 1.83 1.187-1.142 2.705-1.83 4.312-1.83 2.974 0 5.438 1.822 5.438 4.75 0 3.924-2.438 7.11-4.739 9.267a25.18 25.18 0 01-4.244 3.17 15.255 15.255 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <span className="text-[11px] font-extrabold uppercase tracking-wider text-[var(--secondary-500,#FF63B5)]">
                        {t("shoplist.playlist_label", { defaultValue: "SHOPLIST" })}
                      </span>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate mt-0.5">
                        {activeShoplistObj.title}
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        {products.length}{" "}
                        {products.length === 1
                          ? t("shoplist.product", "product")
                          : t("shoplist.products", "products")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                    <button
                      type="button"
                      onClick={() => handleRename(activeShoplistObj)}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-xs transition"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                        <path d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z" />
                        <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z" />
                      </svg>
                      {t("common.rename", { defaultValue: "Rename" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(activeShoplistObj)}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-xl bg-white border border-red-200 text-red-600 hover:bg-red-50 shadow-xs transition"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                        <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5a.75.75 0 0 1 .75-.75Zm3.84.75a.75.75 0 0 0-1.5 0v6.5a.75.75 0 0 0 1.5 0v-6.5Z" clipRule="evenodd" />
                      </svg>
                      {t("common.delete", { defaultValue: "Delete" })}
                    </button>
                  </div>
                </div>
              )}

              {/* Products Section */}
              {loadingProducts ? (
                <div className="flex items-center justify-center py-20 bg-white rounded-2xl border border-gray-200/80 shadow-xs">
                  <div className="w-12 h-12 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200 shadow-xs px-4">
                  <div className="w-16 h-16 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-4 text-pink-500">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-bold text-base">
                    {t("shoplist.no_products_in_list", {
                      defaultValue: `No favourite products in "${activeShoplistObj?.title || "this shoplist"}" yet`,
                    })}
                  </p>
                  <p className="text-gray-400 text-xs sm:text-sm mt-1 max-w-md mx-auto">
                    {t("shoplist.explore_tip", {
                      defaultValue: "When browsing products, click the heart icon to choose and save them into this shoplist.",
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/user/search")}
                    className="mt-5 px-5 py-2.5 text-xs sm:text-sm font-semibold text-white bg-[var(--secondary-500,#FF63B5)] hover:opacity-95 rounded-xl shadow-md shadow-pink-200 transition"
                  >
                    {t("shoplist.explore_products", { defaultValue: "Explore Products" })}
                  </button>

                  {shoplists.some((s) => (s.totalProducts || 0) > 0) && (
                    <div className="mt-6 pt-5 border-t border-gray-100 flex flex-wrap items-center justify-center gap-2">
                      <span className="text-xs text-gray-500 font-medium">
                        {t("shoplist.switch_to", { defaultValue: "Shoplists with saved products:" })}
                      </span>
                      {shoplists
                        .filter((s) => (s.totalProducts || 0) > 0)
                        .map((s) => (
                          <button
                            key={s._id}
                            type="button"
                            onClick={() => setActiveShoplistId(String(s._id))}
                            className="px-3 py-1 text-xs font-semibold rounded-lg bg-pink-50 text-pink-600 hover:bg-pink-100 border border-pink-100 transition shadow-2xs"
                          >
                            {s.title} ({s.totalProducts})
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-5">
                    {currentItems.map((item, index) => (
                      <div
                        key={item._id || index}
                        className="flex flex-col cursor-pointer transition-transform hover:scale-[1.02]"
                        onClick={() => navigate(`/user/product-details?o_id=${item?._id}`)}
                      >
                        <Card
                          images={item.image}
                          compressedImage={item.compressed_image}
                          title={item.title}
                          subtitle={item.description}
                          price={item.price}
                          isAskForPrice={item.isAskForPrice}
                          time={null}
                          live={false}
                          isLiked={true}
                          onLikeClick={(e) => {
                            e.stopPropagation();
                            handleRemoveProduct(item);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  {renderPagination()}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Shoplist;
